import type {
  TornadoWorkerRequest,
  TornadoWorkerResponse
} from "./tornadoWorkerProtocol";

export type PoolTask = {
  promise: Promise<unknown>;
  // Post a follow-up message to the worker running this task.
  send: (message: TornadoWorkerRequest) => void;
  // Drop the task; a worker already running it is terminated, since a
  // synchronous calculation can't be interrupted any other way.
  cancel: () => void;
};

type Task = {
  id: string;
  messages: TornadoWorkerRequest[];
  worker?: Worker;
  done: boolean;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};

export class TornadoWorkerPool {
  private idle: Worker[] = [];
  private workerCount = 0;
  private queue: Task[] = [];
  private running = new Map<Worker, Task>();

  private readonly size: number;
  private readonly createWorker: () => Promise<Worker>;

  constructor(size: number, createWorker: () => Promise<Worker>) {
    this.size = size;
    this.createWorker = createWorker;
  }

  run(message: TornadoWorkerRequest): PoolTask {
    let resolveTask!: (value: unknown) => void;
    let rejectTask!: (reason: unknown) => void;
    const promise = new Promise((resolve, reject) => {
      resolveTask = resolve;
      rejectTask = reject;
    });
    // Callers that cancel don't always await; keep rejections from surfacing
    // as unhandled.
    promise.catch(() => undefined);
    const task: Task = {
      id: message.id,
      messages: [message],
      done: false,
      resolve: resolveTask,
      reject: rejectTask
    };
    this.queue.push(task);
    this.pump();

    return {
      promise,
      send: next => {
        if (task.done) return;
        if (task.worker) task.worker.postMessage(next);
        else task.messages.push(next);
      },
      cancel: () => this.cancel(task)
    };
  }

  private cancel(task: Task) {
    if (task.done) return;
    task.done = true;
    task.reject(new DOMException("Tornado chart job cancelled", "AbortError"));
    const queued = this.queue.indexOf(task);
    if (queued !== -1) this.queue.splice(queued, 1);
    if (task.worker) this.discard(task.worker);
    this.pump();
  }

  private discard(worker: Worker) {
    worker.terminate();
    this.running.delete(worker);
    const idleIndex = this.idle.indexOf(worker);
    if (idleIndex !== -1) this.idle.splice(idleIndex, 1);
    this.workerCount--;
  }

  private async pump() {
    while (this.queue.length > 0) {
      let worker = this.idle.pop();
      if (!worker) {
        if (this.workerCount >= this.size) return;
        this.workerCount++;
        try {
          worker = await this.spawn();
        } catch (err) {
          this.workerCount--;
          this.queue.splice(0).forEach(task => task.reject(err));
          return;
        }
      }
      const task = this.queue.shift();
      if (!task) {
        this.idle.push(worker);
        return;
      }
      task.worker = worker;
      this.running.set(worker, task);
      task.messages.forEach(m => worker!.postMessage(m));
      task.messages = [];
    }
  }

  private async spawn(): Promise<Worker> {
    const worker = await this.createWorker();
    worker.addEventListener(
      "message",
      (event: MessageEvent<TornadoWorkerResponse>) => {
        const task = this.running.get(worker);
        if (!task || task.id !== event.data.id) return;
        this.running.delete(worker);
        task.done = true;
        if (event.data.type === "result") task.resolve(event.data.value);
        else task.reject(new Error(event.data.message));
        this.idle.push(worker);
        this.pump();
      }
    );
    worker.addEventListener("error", event => {
      const task = this.running.get(worker);
      this.discard(worker);
      if (task && !task.done) {
        task.done = true;
        task.reject(new Error(event.message || "Tornado chart worker failed"));
      }
      this.pump();
    });
    return worker;
  }
}
